import type { CSSProperties } from "react";
import { useMemo } from "react";
import useGetUsers from "src/generator/hooks/useGetUsers";
import { mapUsers } from "src/generator/utils/mapUsers";
import { useSelector } from "src/redux/hooks";

import { ListInput } from "../ListInput/ListInput";

interface UsersInputProps {
  id: string;
  value: string;
  roles?: string[];
  placeholder?: string;
  readonly?: boolean;
  textStyle?: CSSProperties;
  onChange: (value: string) => void;
}

export function UsersInput({
  id,
  value,
  roles,
  placeholder = "Select User",
  readonly,
  textStyle,
  onChange,
}: UsersInputProps) {
  const displayPlaceholder =
    readonly && placeholder === "Select User" ? " " : placeholder;

  const defaultUsers = useSelector(state => state.generatorSlice.users) || [];
  const users = useGetUsers({ unit: undefined, defaultUsers });

  const options = useMemo(() => {
    // Return mapped options based on simple mapped user list
    const mapped = mapUsers(
      users,
      roles ?? [],
      "role - firstname surname",
      "role - firstname surname",
    );

    const opts = mapped.map(u => ({
      label: typeof u.dropdownLabel === "string" ? u.dropdownLabel : u.label,
      value: u.value,
    }));

    if (value) {
      const valueIsJson = value.startsWith("{") && value.endsWith("}");
      let parsedUser: any = null;
      let valId = value;

      if (valueIsJson) {
        try {
          parsedUser = JSON.parse(value);
          valId = parsedUser.userid;
        } catch (e) {}
      }

      const hasOption = opts.some(o => o.value === valId);

      if (!hasOption) {
        if (parsedUser) {
          const role = parsedUser.rolename || parsedUser.role || "";
          const first = parsedUser.firstname || "";
          const last = parsedUser.surname || "";
          const labelParts = [role, `${first} ${last}`.trim()].filter(Boolean);
          const label = labelParts.join(" - ").trim() || "Unknown User";
          opts.push({ label, value: valId });
        } else {
          opts.push({ label: value, value: valId });
        }
      }
    }

    return opts;
  }, [users, roles, value]);

  const listValue = useMemo(() => {
    if (value && value.startsWith("{") && value.endsWith("}")) {
      try {
        return JSON.parse(value).userid || value;
      } catch (e) {}
    }
    return value;
  }, [value]);

  const handleChange = (valId: string) => {
    // Attempt to stringify the full user from our local store if they select it
    const fullUser = users.find(u => u.userid === valId);
    if (fullUser) {
      onChange(JSON.stringify(fullUser));
    } else {
      // Fallback: If we don't have the user array loaded, just pass the ID or original stringified object
      const isOriginalSelection = listValue === valId;
      onChange(isOriginalSelection ? value : valId);
    }
  };

  return (
    <ListInput
      id={id}
      value={listValue}
      options={options}
      placeholder={displayPlaceholder}
      readonly={readonly}
      textStyle={textStyle}
      onChange={handleChange}
    />
  );
}
